/**
 * Call feature bootstrap. Loaded on every authenticated page (in the base
 * bundle), it is deliberately light: it wires the websocket call events to the
 * state machine and exposes a small public surface for the thread header. The
 * heavy media path (livekit-client) is only pulled in lazily by call-state when
 * a call actually starts.
 */

import { callState, hasAfterCallScreen } from './call-state.js';
import { callApi } from './call-api.js';
import { afterCallScreenOpen, shortName } from './call-ui.js';
import { createRingAlert } from './ring-alert.js';

function t(key, params) {
    return window.tOr(key, key, params);
}

function detail(event) {
    return (event && event.detail) || {};
}

function registerSocketBridge() {
    document.addEventListener('ws:call-ring', (e) => {
        const d = detail(e);
        callState.incomingRing(d.call, d.caller, d.livekit_url);
    });

    document.addEventListener('ws:call-accept', (e) => {
        const d = detail(e);
        callState.remoteAccepted(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-decline', (e) => {
        const d = detail(e);
        callState.remoteDeclined(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-cancel', (e) => {
        const d = detail(e);
        callState.remoteCanceled(d.call_id, d.state_version, d.reason);
    });

    document.addEventListener('ws:call-answered-elsewhere', (e) => {
        const d = detail(e);
        callState.answeredElsewhere(d.call_id, d.state_version);
    });

    document.addEventListener('ws:call-end', (e) => {
        const d = detail(e);
        callState.remoteEnded(d.call_id, d.state_version, d.duration);
    });

    document.addEventListener('ws:call-auth-accepted', (e) => {
        const d = detail(e);
        const name = d.sender?.name || d.sender?.username || '';
        window.showSuccess?.(t('common.calls_toast_auth_accepted', { username: shortName(name) }));
    });
    // ws:call-auth-request is surfaced by the persistent notification + push and
    // consumed by the thread buttons for a live refresh, so no toast here.

    // A cancel/decline/end delivered while the socket was down is lost, so on
    // every (re)connect the state machine re-fetches the call it is busy with
    // and closes it locally if the backend already did. No-op when idle.
    document.addEventListener('ws:connected', () => {
        callState.reconcileAfterReconnect();
    });
}

function registerEndedToasts() {
    callState.on('ended', (summary) => {
        // The in-call overlay shows its own after-call screen for these.
        if (hasAfterCallScreen(summary)) {
            return;
        }
        const username = summary.other?.username || '';
        const name = summary.other?.name || username;
        const avatar = summary.other?.avatar || '';
        const isVideo = summary.type === 'video';

        switch (summary.toast) {
            case 'missed':
                window.showPushNotification?.({
                    username,
                    url: username ? '/' + username : '',
                    img_url: avatar,
                    title: isVideo ? t('common.calls_toast_missed_video') : t('common.calls_toast_missed_audio'),
                    text: shortName(name),
                });
                break;
            case 'ended':
                window.showPushNotification?.({
                    username,
                    url: username ? '/' + username : '',
                    img_url: avatar,
                    title: t('common.calls_toast_ended'),
                    text: shortName(name),
                });
                break;
            case 'declined':
                window.showInfo?.(t('common.calls_toast_declined', { username: shortName(name) }));
                break;
            case 'no_answer':
                window.showInfo?.(t('common.calls_toast_no_answer'));
                break;
            case 'canceled':
                window.showInfo?.(t('common.calls_toast_canceled'));
                break;
            case 'connect_failed':
                window.showError?.(t('common.calls_toast_connect_failed'));
                break;
            case 'unavailable':
                window.showError?.(t('common.calls_toast_unavailable'));
                break;
            default:
                break;
        }
    });
}

/**
 * Field watchdog for the call surfaces' fail-closed contract: while the call
 * state machine is idle, every element tagged data-call-surface (the incoming
 * modal and the in-call overlay, both full-viewport fixed roots) must have
 * computed display:none. A surface visible while idle paints over the whole
 * page but is invisible to server-side monitoring, so it is reported from the
 * client. The known failure class is a CSS-cascade regression: a truthy
 * display utility on a toggle root is !important (tailwind.config.js
 * important: true) and overrides x-cloak, the inline display:none, and
 * x-show's inline toggling.
 */
const SURFACE_GUARD_DELAY_MS = 3000;

function checkIdleCallSurfacesHidden() {
    if (callState.snapshot().status !== 'idle') {
        return;
    }
    const leaking = [];
    document.querySelectorAll('[data-call-surface]').forEach((el) => {
        const display = window.getComputedStyle(el).display;
        if (display === 'none') {
            return;
        }
        leaking.push({
            surface: el.getAttribute('data-call-surface'),
            display,
            className: el.className,
            inlineStyle: el.getAttribute('style') || '',
        });
        // Self-heal: an inline !important declaration outranks author-level
        // !important rules, so this hides the surface no matter what the
        // stylesheet says.
        el.style.setProperty('display', 'none', 'important');
    });
    if (leaking.length === 0) {
        return;
    }
    if (window.Sentry && typeof window.Sentry.captureMessage === 'function') {
        window.Sentry.captureMessage('calls: surface visible while call state is idle', {
            level: 'error',
            tags: { feature: 'calls', guard: 'call-surface-visibility' },
            extra: { surfaces: leaking },
        });
    } else {
        console.error('calls: surface visible while call state is idle', leaking);
    }
}

function registerSurfaceVisibilityGuard() {
    // One check per page load, shortly after boot so Alpine has initialized
    // and any pre-boot hiding (x-cloak) is out of the picture. No-op on pages
    // without the surfaces (calls gate off).
    const arm = () => setTimeout(checkIdleCallSurfacesHidden, SURFACE_GUARD_DELAY_MS);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', arm, { once: true });
    } else {
        arm();
    }
}

function registerRingAlert() {
    const alert = createRingAlert();
    callState.subscribe((s) => alert.update(s));
}

registerSocketBridge();
registerEndedToasts();
registerSurfaceVisibilityGuard();
registerRingAlert();

// notification-system.js holds back every toast but errors while this is true.
window.callSurfaceActive = () => callState.isBusy() || afterCallScreenOpen();

/**
 * Revoke call authorization for the open conversation's contact (the thread
 * header "turn off calls" control). Reversible: the other side can request
 * again. Refreshes the affordance in place.
 */
async function stopCalls() {
    const uid = window.get_active_thread_uid && window.get_active_thread_uid();
    if (!uid) {
        return;
    }
    try {
        await callApi.revokeAuthorization(uid);
        window.showSuccess?.(t('common.calls_toast_calls_off'));
        document.dispatchEvent(new CustomEvent('calls:authorization-changed', {
            detail: { userId: String(uid) },
        }));
    } catch (e) {
        window.showError?.(t('common.calls_error_unavailable'));
    }
}

// Public surface for the thread header and any other caller.
window.InterpalsCalls = {
    startCall: (targetId, type, other) => callState.startOutgoing(targetId, type, other),
    requestAuthorization: (targetId) => callApi.requestAuthorization(targetId),
    revokeAuthorization: (userId) => callApi.revokeAuthorization(userId),
    stopCalls,
    state: callState,
};
